import type { TaskPriority } from '../../../lib/tasksApi';
import type { TaskStatusCategory } from '../../../lib/taskStatusesApi';
import { translate } from '../../../lib/i18n';

/**
 * CLE-174 — shared visual primitives for the Linear-clone task list.
 *
 * Status and priority icons are inline SVGs so they render crisply at the
 * small sizes Linear uses (12–16px) and pick up colour from props rather than
 * Tailwind classes. Keeping them here means the row, the group header, and the
 * inline popovers all draw the exact same glyph.
 *
 * Statuses are configurable DB rows (CLE-DASH): the glyph is keyed on the
 * status CATEGORY and the accent colour comes from the row's stored hex.
 */

interface IconProps {
  size?: number;
  className?: string;
}

/**
 * Warning triangle (Linear marks a status group that contains attention-needing
 * work — e.g. overdue items — with a small amber triangle). 12px, #eab308.
 */
export function WarningTriangleIcon({ size = 12, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M6 1.2 11 10.2H1L6 1.2Z"
        fill="none"
        stroke="#eab308"
        strokeWidth={1.2}
        strokeLinejoin="round"
      />
      <rect x={5.4} y={4.3} width={1.2} height={3} rx={0.6} fill="#eab308" />
      <rect x={5.4} y={8} width={1.2} height={1.2} rx={0.6} fill="#eab308" />
    </svg>
  );
}

/**
 * Status icon — a 14px ring whose fill communicates progress, keyed on the
 * status CATEGORY (statuses themselves are user-configurable):
 *  - BACKLOG: dashed ring
 *  - UNSTARTED: empty ring
 *  - STARTED: partially-filled pie
 *  - COMPLETED: filled disc + check
 *  - CANCELLED: filled disc + ✕
 */
export function StatusIcon({
  category,
  color,
  size = 14,
}: IconProps & { category: TaskStatusCategory; color: string }) {
  const c = size / 2;
  const r = c - 1.5;
  const common = { cx: c, cy: c, r };

  if (category === 'BACKLOG') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle {...common} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="2 2" />
      </svg>
    );
  }

  if (category === 'UNSTARTED') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle {...common} fill="none" stroke={color} strokeWidth={1.5} />
      </svg>
    );
  }

  if (category === 'COMPLETED') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={c} cy={c} r={c} fill={color} />
        <path
          d={`M${c * 0.6} ${c} L${c * 0.9} ${c * 1.3} L${c * 1.45} ${c * 0.72}`}
          fill="none"
          stroke="#ffffff"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (category === 'CANCELLED') {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={c} cy={c} r={c} fill={color} />
        <path
          d={`M${c * 0.65} ${c * 0.65} L${c * 1.35} ${c * 1.35} M${c * 1.35} ${c * 0.65} L${
            c * 0.65
          } ${c * 1.35}`}
          stroke="#ffffff"
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      </svg>
    );
  }

  // STARTED draws a partial pie inside the ring.
  const fraction = 0.5;
  const innerR = r - 1;
  const angle = fraction * 2 * Math.PI;
  // Sweep arc starting at 12 o'clock, clockwise.
  const startX = c;
  const startY = c - innerR;
  const endX = c + innerR * Math.sin(angle);
  const endY = c - innerR * Math.cos(angle);
  const largeArc = fraction > 0.5 ? 1 : 0;
  const pie = `M${c} ${c} L${startX} ${startY} A${innerR} ${innerR} 0 ${largeArc} 1 ${endX} ${endY} Z`;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle {...common} fill="none" stroke={color} strokeWidth={1.5} />
      <path d={pie} fill={color} />
    </svg>
  );
}

// --- Priority ---------------------------------------------------------------

export const PRIORITY_LABEL: Record<TaskPriority, string> = new Proxy(
  {} as Record<TaskPriority, string>,
  {
    get: (_t, prop: string) => translate(`tasks.priority.${prop}`),
  },
);

export const PRIORITY_COLOR: Record<TaskPriority, string> = {
  URGENT: '#ef4444',
  HIGH: '#f97316',
  MEDIUM: '#eab308',
  LOW: '#9ca3af',
  NONE: '#6b7280',
};

// Priority display order for the popover (Urgent → No priority).
export const PRIORITY_MENU_ORDER: TaskPriority[] = ['URGENT', 'HIGH', 'MEDIUM', 'LOW', 'NONE'];

/**
 * Priority icon — Linear uses an orange/red square with "!" for Urgent and a
 * three-bar signal glyph (1/2/3 lit bars) for Low/Medium/High. No-priority is a
 * muted horizontal line.
 */
export function PriorityIcon({ priority, size = 16 }: IconProps & { priority: TaskPriority }) {
  const color = PRIORITY_COLOR[priority];

  if (priority === 'URGENT') {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
        <rect x={1} y={1} width={14} height={14} rx={3} fill={color} />
        <rect x={7} y={3.5} width={2} height={6} rx={1} fill="#ffffff" />
        <rect x={7} y={11} width={2} height={2} rx={1} fill="#ffffff" />
      </svg>
    );
  }

  if (priority === 'NONE') {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
        <rect x={3} y={7} width={10} height={2} rx={1} fill={color} />
      </svg>
    );
  }

  // Signal bars: number of lit bars depends on level.
  const lit = priority === 'HIGH' ? 3 : priority === 'MEDIUM' ? 2 : 1;
  const bars = [
    { x: 2, h: 5, y: 9 },
    { x: 6.5, h: 9, y: 5 },
    { x: 11, h: 13, y: 1 },
  ];
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      {bars.map((b, i) => (
        <rect
          key={b.x}
          x={b.x}
          y={b.y}
          width={3}
          height={b.h}
          rx={1}
          fill={i < lit ? color : 'currentColor'}
          opacity={i < lit ? 1 : 0.25}
        />
      ))}
    </svg>
  );
}
